// ============================================
// NOTIFICATION BELL COMPONENT
// components/NotificationBell.jsx
// ============================================

import React, { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { 
  Bell, X, Clock, MessageSquare, AlertTriangle, 
  CheckCircle, IndianRupee, ShoppingCart, XCircle,
  RefreshCw, Shield, Megaphone, Package
} from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import api from '../../services/api'
import {
  getUnreadCount,
  subscribeUnreadCount,
  setUnreadCount,
  refreshUnreadCount,
} from '../../services/unreadCountStore'

const NotificationBell = () => {
  const [open, setOpen] = useState(false)
  const [notifications, setNotifications] = useState([])
  // Mirrors the shared store rather than owning the number: the Navbar mounts
  // this component twice (desktop + mobile), and two copies of one count must
  // not mean two of every request.
  const [unreadCount, setLocalUnreadCount] = useState(getUnreadCount)
  const [loading, setLoading] = useState(false)
  const [selectedNotification, setSelectedNotification] = useState(null)
  const navigate = useNavigate()

  // Fetch notifications
  const fetchNotifications = async () => {
    try {
      setLoading(true)
      const response = await api.get('/api/notifications/my')
      const sorted = [...response.data.notifications].sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
      )
      setNotifications(sorted)
      setUnreadCount(response.data.unread_count)
    } catch (error) {
      console.error('Failed to fetch notifications:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    // NO TIMER. This badge is fetched when the component mounts, when the tab
    // is returned to, and when connectivity comes back — and at no other time.
    //
    // It was 30s (~8,000 calls/day, the busiest endpoint on the platform), then
    // 2 minutes. Even at 2 minutes the three nav badges together were 11% of
    // every request the platform served, purely from tabs sitting open.
    //
    // Nothing is lost by removing the timer. A count nobody is looking at does
    // not need to be correct, and the moment someone looks — switching to the
    // tab, or opening the app — the visibility listener has already refreshed
    // it. The only case a timer served is a badge changing while you watch it,
    // which for an unread count is not worth 11% of the bill. Genuinely urgent
    // notifications arrive by push, which does not poll for anything.
    //
    // The request itself lives in unreadCountStore, so the second mounted bell
    // joins this one instead of duplicating it.
    const unsubscribe = subscribeUnreadCount(setLocalUnreadCount)
    setLocalUnreadCount(getUnreadCount())
    refreshUnreadCount()

    const onVisible = () => { if (document.visibilityState === 'visible') refreshUnreadCount() }
    // Coming back online is exactly when the last attempt is most likely to
    // have been the one that failed, so this one ignores the freshness window.
    const onOnline = () => refreshUnreadCount({ force: true })
    document.addEventListener('visibilitychange', onVisible)
    window.addEventListener('online', onOnline)

    return () => {
      unsubscribe()
      document.removeEventListener('visibilitychange', onVisible)
      window.removeEventListener('online', onOnline)
    }
  }, [])

  // Fetch full notifications when drawer opens
  useEffect(() => {
    if (open) {
      fetchNotifications()
    }
  }, [open])

  // Mark notification as read
  const markAsRead = async (notificationId) => {
    try {
      await api.patch(`/api/notifications/${notificationId}`, {
        status: 'read'
      })
      
      // Update local state
      setNotifications(notifications.map(n => 
        n.notification_id === notificationId 
          ? { ...n, status: 'read' }
          : n
      ))
      // Read through the store, not local state: the other mounted bell has to
      // move too, and the store clamps at zero.
      setUnreadCount(getUnreadCount() - 1)
    } catch (error) {
      console.error('Failed to mark notification as read:', error)
    }
  }

  // Mark all as read
  const markAllAsRead = async () => {
    try {
      await api.post('/api/notifications/mark-all-read')
      setNotifications(notifications.map(n => ({ ...n, status: 'read' })))
      setUnreadCount(0)
    } catch (error) {
      console.error('Failed to mark all as read:', error)
    }
  }

  // Handle notification click
  const handleNotificationClick = (notification) => {
    // Mark as read
    if (notification.status === 'unread') {
      markAsRead(notification.notification_id)
    }

    // Show full notification in a modal
    setSelectedNotification(notification)
  }

  // Navigate to the notification's link from the modal
  const handleViewLink = (notification) => {
    if (notification.link) {
      navigate(notification.link)
    }
    setSelectedNotification(null)
    setOpen(false)
  }

  // Delete notification
  const deleteNotification = async (notificationId, e) => {
    e.stopPropagation()
    
    try {
      await api.delete(`/api/notifications/${notificationId}`)
      setNotifications(notifications.filter(n => n.notification_id !== notificationId))
    } catch (error) {
      console.error('Failed to delete notification:', error)
    }
  }

  return (
    <>
      {/* Bell Icon */}
      <button
        onClick={() => setOpen(true)}
        className="relative p-2 rounded-full hover:bg-gray-100 transition-all duration-200"
      >
        <Bell className="w-5 h-5 text-gray-700" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center animate-pulse">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Notification Drawer */}
      {open && createPortal(
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[9998] transition-opacity"
            onClick={() => setOpen(false)}
          />

          {/* Drawer */}
          <div className="fixed top-0 right-0 h-full w-full sm:w-[420px] bg-white z-[9999] shadow-2xl animate-slide-in-right flex flex-col">
            {/* Header — full-height drawer starts at y=0, so in the installed
                app it begins under the status bar without this inset. */}
            <div className="flex items-center justify-between px-5 pb-4 pt-drawer-t border-b bg-gradient-to-r from-emerald-50 to-teal-50">
              <div>
                <h3 className="text-lg font-bold text-gray-900">Notifications</h3>
                <p className="text-xs text-gray-500">
                  {unreadCount > 0 ? `${unreadCount} unread` : 'All caught up! 🎉'}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {unreadCount > 0 && (
                  <button
                    onClick={markAllAsRead}
                    className="text-xs text-emerald-600 hover:text-emerald-700 font-semibold"
                  >
                    Mark all read
                  </button>
                )}
                <button 
                  onClick={() => setOpen(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
            </div>

            {/* Notifications List */}
            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="p-8 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500 mx-auto"></div>
                  <p className="text-sm text-gray-500 mt-4">Loading notifications...</p>
                </div>
              ) : notifications.length === 0 ? (
                <div className="p-12 text-center">
                  <Bell className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500 font-medium">No notifications yet</p>
                  <p className="text-xs text-gray-400 mt-2">
                    We'll notify you when something important happens
                  </p>
                </div>
              ) : (
                <div className="divide-y">
                  {notifications.map(notification => (
                    <NotificationItem
                      key={notification.notification_id}
                      notification={notification}
                      onClick={() => handleNotificationClick(notification)}
                      onDelete={(e) => deleteNotification(notification.notification_id, e)}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </>,
        document.body
      )}

      {/* Full Notification Modal */}
      {selectedNotification && createPortal(
        <NotificationModal
          notification={selectedNotification}
          onClose={() => setSelectedNotification(null)}
          onView={() => handleViewLink(selectedNotification)}
        />,
        document.body
      )}
    </>
  )
}

// ============================================
// NOTIFICATION MODAL COMPONENT
// ============================================

const NotificationModal = ({ notification, onClose, onView }) => {
  const Icon = getNotificationIcon(notification.type)
  const colorClass = getNotificationColor(notification.type)

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[10000] flex items-center justify-center p-4"
        onClick={onClose}
      >
        {/* Modal */}
        <div
          className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[85vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-start gap-3 px-6 pt-6 pb-4">
            <div className={`w-11 h-11 rounded-full flex-shrink-0 flex items-center justify-center ${colorClass}`}>
              <Icon className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-base font-bold text-gray-900">
                  {notification.title}
                </h3>
                {notification.priority === 'urgent' && (
                  <span className="px-2 py-0.5 bg-red-100 text-red-700 text-[10px] font-bold rounded-full">
                    URGENT
                  </span>
                )}
              </div>
              <p className="text-[11px] text-gray-400 flex items-center gap-1 mt-1">
                <Clock className="w-3 h-3" />
                {notification.time}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 -mt-1 -mr-1 hover:bg-gray-100 rounded-lg transition flex-shrink-0"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Full message */}
          <div className="px-6 pb-6">
            <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
              {notification.message}
            </p>
          </div>

          {/* Actions */}
          {notification.link && (
            <div className="px-6 pb-6 flex justify-end gap-2 border-t pt-4">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-100 rounded-lg transition"
              >
                Close
              </button>
              <button
                onClick={onView}
                className="px-4 py-2 text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition"
              >
                View Details
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  )
}

// ============================================
// NOTIFICATION ITEM COMPONENT
// ============================================

const NotificationItem = ({ notification, onClick, onDelete }) => {
  const Icon = getNotificationIcon(notification.type)
  const colorClass = getNotificationColor(notification.type)
  const priorityClass = getPriorityClass(notification.priority)

  return (
    <div
      onClick={onClick}
      className={`px-5 py-4 cursor-pointer hover:bg-gray-50 transition-all duration-200 ${
        notification.status === 'unread' ? 'bg-blue-50/50 border-l-4 border-emerald-500' : ''
      }`}
    >
      <div className="flex gap-3">
        {/* Icon */}
        <div className={`w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center ${colorClass}`}>
          <Icon className="w-5 h-5 text-white" />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between mb-1">
            <p className="text-sm font-semibold text-gray-900 leading-tight">
              {notification.title}
            </p>
            {notification.priority === 'urgent' && (
              <span className="ml-2 px-2 py-0.5 bg-red-100 text-red-700 text-[10px] font-bold rounded-full flex-shrink-0">
                URGENT
              </span>
            )}
          </div>
          
          <p className="text-xs text-gray-600 mb-2 line-clamp-2">
            {notification.message}
          </p>
          
          <div className="flex items-center justify-between">
            <p className="text-[10px] text-gray-400 flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {notification.time}
            </p>
            
            <button
              onClick={onDelete}
              className="text-[10px] text-red-600 hover:text-red-700 font-semibold opacity-0 group-hover:opacity-100 transition"
            >
              Delete
            </button>
          </div>
        </div>

        {/* Unread indicator */}
        {notification.status === 'unread' && (
          <div className="w-2 h-2 bg-emerald-500 rounded-full flex-shrink-0 mt-1 animate-pulse" />
        )}
      </div>
    </div>
  )
}

// ============================================
// HELPER FUNCTIONS
// ============================================

const getNotificationIcon = (type) => {
  const icons = {
    support_reply: MessageSquare,
    support_resolved: CheckCircle,
    support_sla_risk: AlertTriangle,
    payout_initiated: IndianRupee,
    payout_completed: CheckCircle,
    payout_failed: XCircle,
    sale_made: ShoppingCart,
    refund_processed: RefreshCw,
    asset_approved: CheckCircle,
    asset_rejected: XCircle,
    account_suspended: AlertTriangle,
    account_reactivated: CheckCircle,
    bank_verified: Shield,
    bank_rejected: XCircle,
    admin_message: Bell,
    system_announcement: Megaphone,
    instagram_autodm_enabled: MessageSquare,
    instagram_autodm_disabled: XCircle,
    autodm_subscription_granted: Shield,
    autodm_subscription_revoked: XCircle,
    // Review My Work
    review_order_received: ShoppingCart,
    review_started: Clock,
    review_submitted: Package,
    review_uploaded: CheckCircle,
    review_deadline_24h: Clock,
    review_deadline_12h: Clock,
    review_deadline_2h: AlertTriangle,
    review_late: AlertTriangle,
    review_completed: CheckCircle,
    review_refunded: RefreshCw
  }
  return icons[type] || Bell
}

const getNotificationColor = (type) => {
  const colors = {
    support_reply: 'bg-green-500',
    support_resolved: 'bg-green-500',
    support_sla_risk: 'bg-red-500',
    payout_initiated: 'bg-blue-500',
    payout_completed: 'bg-green-500',
    payout_failed: 'bg-red-500',
    sale_made: 'bg-emerald-500',
    refund_processed: 'bg-orange-500',
    asset_approved: 'bg-green-500',
    asset_rejected: 'bg-red-500',
    account_suspended: 'bg-red-500',
    account_reactivated: 'bg-green-500',
    bank_verified: 'bg-green-500',
    bank_rejected: 'bg-red-500',
    admin_message: 'bg-purple-500',
    system_announcement: 'bg-blue-500',
    instagram_autodm_enabled: 'bg-emerald-500',
    instagram_autodm_disabled: 'bg-gray-500',
    autodm_subscription_granted: 'bg-violet-500',
    autodm_subscription_revoked: 'bg-orange-500',
    // Review My Work
    review_order_received: 'bg-emerald-500',
    review_started: 'bg-blue-500',
    review_submitted: 'bg-blue-500',
    review_uploaded: 'bg-green-500',
    review_deadline_24h: 'bg-amber-500',
    review_deadline_12h: 'bg-orange-500',
    review_deadline_2h: 'bg-red-500',
    review_late: 'bg-red-500',
    review_completed: 'bg-green-500',
    review_refunded: 'bg-orange-500'
  }
  return colors[type] || 'bg-gray-500'
}

const getPriorityClass = (priority) => {
  const classes = {
    urgent: 'border-red-500',
    high: 'border-orange-500',
    medium: 'border-blue-500',
    low: 'border-gray-500'
  }
  return classes[priority] || ''
}

export default NotificationBell

// ============================================
// CSS ANIMATIONS
// Add to your global CSS or Tailwind config
// ============================================

/*
@keyframes slide-in-right {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

.animate-slide-in-right {
  animation: slide-in-right 0.3s ease-out;
}
*/